import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { captureAnchor, isInsideCommentsUI, UI_ATTR } from './anchor';
import { CommentsProvider, useComments } from './useCommentsState';
import { usePrototypeRole } from '../access/role';
import { FloatingButton } from './FloatingButton';
import { PinLayer } from './PinLayer';
import { CommentsDrawer } from './CommentsDrawer';
import './comments.css';

function LayerInner() {
  const {
    mode,
    draft,
    setDraft,
    toggleOnOff,
    activeThreadId,
    openThread,
    drawerOpen,
    setDrawerOpen,
    navigateToThread,
  } = useComments();
  const location = useLocation();

  const active = mode !== 'off';
  const commenting = mode === 'comment' && !draft;

  // --- modo comentar: cursor crosshair + captura de clique no protótipo ---
  useEffect(() => {
    if (!commenting) return;
    document.body.classList.add('bc-commenting');

    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (isInsideCommentsUI(target)) return; // deixa a UI de comentários funcionar
      e.preventDefault();
      e.stopPropagation();
      setDraft(captureAnchor(e.clientX, e.clientY));
    };
    document.addEventListener('click', onClick, true);

    return () => {
      document.body.classList.remove('bc-commenting');
      document.removeEventListener('click', onClick, true);
    };
  }, [commenting, setDraft]);

  // --- atalhos de teclado ---
  // `C` liga/desliga toda a camada (mesmo desligada). Esc fecha o que estiver
  // aberto sem sair do modo comentário.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t as any).isContentEditable);

      if (e.key === 'Escape') {
        if (draft) setDraft(null);
        else if (activeThreadId) openThread(null);
        else if (drawerOpen) setDrawerOpen(false);
        return;
      }
      // sem modificadores: não sequestra Ctrl+C/Cmd+C (copiar) etc.
      if (
        (e.key === 'c' || e.key === 'C') &&
        !typing &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        toggleOnOff();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draft, activeThreadId, drawerOpen, openThread, setDraft, setDrawerOpen, toggleOnOff]);

  // --- deep-link ?comment=<thread_id> (uma vez por valor) ---
  const handled = useRef<string | null>(null);
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('comment');
    if (id && handled.current !== id) {
      handled.current = id;
      navigateToThread(id);
    }
  }, [location.search, navigateToThread]);

  return (
    <div className="bc-root" {...{ [UI_ATTR]: '' }}>
      <FloatingButton />
      {active && <PinLayer />}
      {active && <CommentsDrawer />}
    </div>
  );
}

/**
 * Ponto de montagem global. Duas condições impedem que QUALQUER coisa monte — sem provider,
 * sem listeners, sem polling, sem requisição:
 *
 * 1. Papel de acesso `client`: comentários são ferramenta interna. Não basta esconder o
 *    FloatingButton — o atalho de teclado `c` (ver LayerInner) ligaria o modo comentário
 *    para o participante do teste, e o provider ficaria fazendo polling da API.
 * 2. Kill switch `?comments=off`, disponível também para o interno.
 */
export function CommentsRoot() {
  const { role } = usePrototypeRole();
  const { search } = useLocation();
  const q = new URLSearchParams(search).get('comments');

  // Persiste a decisão do kill switch além da query string — um navigate() dos
  // layouts descarta o parâmetro, mas a camada deve continuar desligada.
  useEffect(() => {
    try {
      if (q === 'off') sessionStorage.setItem('bc-killed', '1');
      else if (q === 'on') sessionStorage.removeItem('bc-killed');
    } catch (_) {
      /* sessionStorage indisponível */
    }
  }, [q]);

  let persistedKilled = false;
  try {
    persistedKilled = sessionStorage.getItem('bc-killed') === '1';
  } catch (_) {
    /* ignore */
  }
  const killed = q === 'off' || (q !== 'on' && persistedKilled);
  if (killed || role !== 'internal') return null;

  return (
    <CommentsProvider>
      <LayerInner />
    </CommentsProvider>
  );
}
